import { useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { checkinAsset, checkoutAsset, getAsset, listAssetStatuses, listAssignees } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate, formatDateShort, formatTicketNumber } from "../lib/format";
import { useTicketNumberFormat } from "../lib/ticketNumbering";
import { PageBar } from "../components/Layout";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { RichBody, richTextIsEmpty } from "../components/RichText";
import type { AssetAssignmentRow, AssetHolder, AssetRow, AssetTicketRef } from "../lib/types";
import { AssetModal, AssetStatusBadge } from "./AssetsPage";

/** yyyy-mm-dd <input type=date> → unix seconds (local start-of-day), or null. */
function dateInputToUnix(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return null;
  return Math.floor(new Date(y, m - 1, d).getTime() / 1000);
}

/** Small red "Overdue" pill, reused on the header and custody rows. */
function OverduePill() {
  return <span className="asset-status asset-status-overdue" style={{ color: "#b91c1c", borderColor: "#b91c1c" }}>Overdue</span>;
}

export function AssetDetailPage() {
  const { id = "" } = useParams();
  const { data, loading, error, reload } = useApi((signal) => getAsset(id, signal), [id]);
  const statusesApi = useApi((s) => listAssetStatuses(s), []);
  const assetStatuses = statusesApi.data?.asset_statuses ?? [];
  const [editing, setEditing] = useState(false);

  return (
    <>
      <PageBar crumbs="Assets / Detail" />
      <div className="content">
        {loading ? (
          <Loading label="Loading asset…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : !data ? (
          <EmptyState>Asset not found.</EmptyState>
        ) : (
          <>
            <div className="page-head">
              <div>
                <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {data.asset.name} <AssetStatusBadge status={data.asset.status} statuses={assetStatuses} />
                  {data.current_period?.is_overdue ? <OverduePill /> : null}
                </h1>
                <div className="sub">
                  {data.asset.asset_tag ? `Tag ${data.asset.asset_tag}` : "No asset tag"}
                  {data.asset.type_name ? ` · ${data.asset.type_name}` : ""}
                </div>
              </div>
              <button className="btn" onClick={() => setEditing(true)}>
                Edit
              </button>
            </div>

            <div className="grid-2">
              <PropertiesCard asset={data.asset} />
              <PossessionCard asset={data.asset} holders={data.holders} onChanged={reload} />
            </div>

            <CustodyCard assignments={data.assignments} />
            <RelatedTicketsCard tickets={data.tickets} />
          </>
        )}
      </div>

      {editing && data ? (
        <AssetModal
          asset={data.asset}
          statuses={assetStatuses}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function PropertiesCard({ asset }: { asset: AssetRow }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Details</h3>
      </div>
      <div className="card-body">
        <dl className="prop-list">
          <Prop label="Tag" value={asset.asset_tag} />
          <Prop label="Type" value={asset.type_name} />
          <Prop label="Category" value={asset.category_name} />
          <Prop label="Serial" value={asset.serial} />
          <Prop label="Quantity" value={String(asset.quantity ?? 1)} />
          <dt>Notes</dt>
          <dd>
            {asset.notes && !richTextIsEmpty(asset.notes) ? (
              <RichBody html={asset.notes} />
            ) : (
              <span className="muted">—</span>
            )}
          </dd>
        </dl>
      </div>
    </div>
  );
}

function Prop({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value ? value : <span className="muted">—</span>}</dd>
    </>
  );
}

/** Quantity-aware possession: an asset with quantity > 1 can be checked out to
 *  several holders at once. Shows availability, lists each open holder (with a
 *  per-holder check-in), and a check-out form that takes a quantity. Every
 *  change is logged in custody history. */
function PossessionCard({
  asset,
  holders,
  onChanged,
}: {
  asset: AssetRow;
  holders: AssetHolder[];
  onChanged: () => void;
}) {
  const assigneesApi = useApi((signal) => listAssignees(signal), []);
  const assignees = assigneesApi.data?.assignees ?? [];
  // Start on whichever tab has something to do: holders if any are out, else check out.
  const [tab, setTab] = useState<"checkout" | "holders">(holders.length > 0 ? "holders" : "checkout");
  const [userId, setUserId] = useState("");
  const [qty, setQty] = useState("1");
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [reminderDate, setReminderDate] = useState("");

  const checkoutMut = useMutation(
    (input: { user_id: string; quantity: number; note?: string | null; due_at?: number | null; reminder_at?: number | null }) =>
      checkoutAsset(asset.id, input),
  );
  const checkinMut = useMutation((assignmentId: string) => checkinAsset(asset.id, assignmentId));

  const total = asset.quantity ?? 1;
  const available = asset.available ?? 0;
  const bulk = total > 1;
  const anyOverdue = holders.some((h) => h.is_overdue);

  // Header pill: overdue (red) → some out (amber) → available (vivid green).
  // Any availability shows the green "N available" so stock is obvious at a glance.
  const greenPill = { background: "#16a34a", color: "#fff", border: "1px solid #15803d" } as const;
  const pill: { cls: string; label: string; style?: CSSProperties } = anyOverdue
    ? { cls: "pr-urgent", label: "Overdue" }
    : available <= 0
      ? { cls: "st-in-progress", label: "Fully checked out" }
      : { cls: "st-done", label: `${available} available`, style: greenPill };
  const dateRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 } as const;

  async function checkOut() {
    if (!userId) return;
    const n = Math.max(1, Math.min(available, parseInt(qty, 10) || 1));
    const res = await checkoutMut.run({
      user_id: userId,
      quantity: n,
      note: note.trim() || null,
      due_at: dateInputToUnix(dueDate),
      reminder_at: dateInputToUnix(reminderDate),
    });
    if (res) {
      setUserId("");
      setQty("1");
      setNote("");
      setDueDate("");
      setReminderDate("");
      onChanged();
    }
  }
  async function checkIn(assignmentId: string) {
    const res = await checkinMut.run(assignmentId);
    if (res) onChanged();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Possession</h3>
        <span className={`badge ${pill.cls}`} style={{ marginLeft: "auto", ...(pill.style ?? {}) }}>
          {pill.label}
        </span>
      </div>
      <div className="card-body">
        {checkoutMut.error ? <Alert kind="error">{checkoutMut.error}</Alert> : null}
        {checkinMut.error ? <Alert kind="error">{checkinMut.error}</Alert> : null}

        <p style={{ marginTop: 0 }}>
          <strong>{available}</strong> <span className="muted">of</span> <strong>{total}</strong>{" "}
          <span className="muted">available</span>
        </p>

        {/* Tabs — current possessions (holders + check-in) vs. check out. */}
        <div className="seg" role="group" aria-label="Possession view" style={{ marginBottom: 14 }}>
          <button
            type="button"
            className={`seg-btn${tab === "holders" ? " active" : ""}`}
            aria-pressed={tab === "holders"}
            onClick={() => setTab("holders")}
          >
            Current possessions{holders.length > 0 ? ` (${holders.length})` : ""}
          </button>
          <button
            type="button"
            className={`seg-btn${tab === "checkout" ? " active" : ""}`}
            aria-pressed={tab === "checkout"}
            onClick={() => setTab("checkout")}
          >
            Check out
          </button>
        </div>

        {tab === "holders" ? (
          /* Current holders — each open check-out, with its own check-in. */
          holders.length > 0 ? (
            <ul className="person-list">
              {holders.map((h) => (
                <li key={h.assignment_id} className="person-row">
                  <span className="person-main" style={{ minWidth: 0 }}>
                    <span className="person-name">
                      {h.user_name ?? "Unassigned"}
                      {bulk ? <strong> ×{h.quantity}</strong> : null}
                    </span>
                    <span className="muted" style={{ fontSize: 13 }}>
                      {h.is_overdue ? (
                        <span style={{ color: "#b91c1c", fontWeight: 600 }}>Overdue · </span>
                      ) : null}
                      {h.due_at ? `Due ${formatDateShort(h.due_at)}` : "No due date"}
                      {h.note ? ` · ${h.note}` : ""}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn small"
                    style={{ flex: "0 0 auto" }}
                    disabled={checkinMut.pending}
                    onClick={() => checkIn(h.assignment_id)}
                  >
                    Check in
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted" style={{ marginTop: 8 }}>
              Nobody holds this asset right now. All {total} {total === 1 ? "unit is" : "units are"} available.
            </p>
          )
        ) : /* Check-out form — only when there are free units. */
        available > 0 ? (
          <>
            <div className="row" style={{ gap: 10, alignItems: "flex-end" }}>
              <div className="field" style={{ flex: bulk ? 3 : 1 }}>
                <span className="label">Check out to</span>
                <select value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">Select a person…</option>
                  {assignees.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              {bulk ? (
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Qty</span>
                  <input
                    type="number"
                    min={1}
                    max={available}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                  />
                </label>
              ) : null}
            </div>
            <div style={dateRow}>
              <label className="field">
                <span className="label">Due back (optional)</span>
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </label>
              <label className="field">
                <span className="label">Remind on (optional)</span>
                <input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} />
              </label>
            </div>
            <label className="field" style={{ marginTop: 8 }}>
              <span className="label">Note (optional)</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. on loan for fall term"
              />
            </label>
            <button
              className="btn primary"
              style={{ marginTop: 12 }}
              onClick={checkOut}
              disabled={checkoutMut.pending || !userId}
            >
              {checkoutMut.pending ? <Spinner label="Saving…" /> : bulk ? "Check out units" : "Check out"}
            </button>
          </>
        ) : (
          <p className="muted" style={{ marginTop: 8 }}>
            All {total} {total === 1 ? "unit is" : "units are"} checked out. Check one in to free it up.
          </p>
        )}
      </div>
    </div>
  );
}

/** Tickets that reference this asset (link table + legacy asset_id). Read-only
 *  — linking is driven from the ticket side. */
function RelatedTicketsCard({ tickets }: { tickets: AssetTicketRef[] }) {
  const numFmt = useTicketNumberFormat();
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h3>Tickets</h3>
        <span className="faint">{tickets.length}</span>
      </div>
      <div className="card-body">
        {tickets.length === 0 ? (
          <EmptyState>No linked tickets.</EmptyState>
        ) : (
          <ul className="timeline">
            {tickets.map((t) => (
              <li key={t.id} className="timeline-item">
                <Link to={`/tickets/${t.id}`}>
                  {formatTicketNumber(t.number, numFmt)} {t.subject}
                </Link>
                <span className="muted"> · {formatDate(t.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CustodyCard({ assignments }: { assignments: AssetAssignmentRow[] }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h3>Position History</h3>
        <span className="faint">{assignments.length}</span>
      </div>
      <div className="card-body">
        {assignments.length === 0 ? (
          <EmptyState>No check-out history yet.</EmptyState>
        ) : (
          <ul className="timeline">
            {assignments.map((a) => (
              <li key={a.id} className="timeline-item">
                <span className={`asset-evt asset-evt-${a.action}`}>
                  {a.action === "checkout" ? "Checked out" : "Checked in"}
                </span>{" "}
                {a.action === "checkout" ? (
                  <>
                    to <strong>{a.user_name ?? "—"}</strong>
                  </>
                ) : (
                  <>(returned)</>
                )}
                {a.by_user_name ? <span className="muted"> · by {a.by_user_name}</span> : null}
                <span className="muted"> · {formatDate(a.created_at)}</span>
                {a.due_at ? <span className="muted"> · due {formatDate(a.due_at)}</span> : null}
                {a.is_overdue ? <span style={{ color: "#b91c1c", fontWeight: 600 }}> · Overdue</span> : null}
                {a.note ? <div className="muted">{a.note}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

